/* eslint-disable @typescript-eslint/no-unused-vars */
import { SearchModes } from '@/domainLogic/index.ts';
import React from 'react';
import dynamic from 'next/dynamic';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { SUSTAINABILITY_CERTIFICATE_COPY } from '@/app/common/sustainabilityCertificate.ts';
import { getBooleanFromString } from '@/app/components/search/utils/query2.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getShowOvernightFilter } from '@/app/common/filters/groups/getShowOvernightFilter.ts';
import { filterAvailability } from '@/app/common/filters/groups/stripUnavailableParams.ts';
import { bookNowPayLaterAvailable } from '@/app/common/filters/core/filterFlags.ts';
import { disabledAtZero } from '@/app/common/filters/groups/optionCounts.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { type ControlProps, ControlType } from '../../Form';
import type { CuriosityParams } from '../../../Curiosity';
import type { Localization } from '../../../common/Localization';
import type { CommonProps } from './Fields/CommonProps';
import getFacilitiesField from './Fields/getFacilitiesField';
import getPropertyTypesField from './Fields/getPropertyTypesField';
import getPriceField from './Fields/getPriceField';
import getToggleField from './Fields/getToggleField';
import getFlightField from './Fields/getFlightField';
import getLeisureField from './Fields/getLeisureField';
import getStarsField from './Fields/getStarsField';
import getMealPlanContainer from './Fields/getMealPlanField/getMealPlanField';
import getRatingField from './Fields/getRatingField';
import { getBeachTypeField } from './Fields/getBeachTypeField';
import getClimateField from './Fields/getClimateField';
import getFaunaField from './Fields/getFaunaField';
import getWaterSportsField from './Fields/getWaterSportsField';
import getEntertainmentsField from './Fields/getEntertainmentsField';
import getWaterWavesField from './Fields/getWaterWavesField';
import getDestinationAirportField from './Fields/getDestinationAirportField';
import FilterContainer from './FilterContainer';
import type { DynamicFiltersResponse } from '../../../utils/api/dynamicFiltersResponseTypes';
import getDiscountFields from './Fields/getDiscountFields';
import getDynamicField, { getAccommodationThemesOptions } from './Fields/getDynamicField';
import getPropertyNameField from './Fields/getProprtyNameField';
import { isEmptyObject } from '../../../Utilities';
import { shouldShowPropertyNameFilter } from '../../../utils/api/isLiveSearchByDate';
import getOvernightFlights from './Fields/getOvernightFlightsField';
import getSortControl from './Fields/getSortControl';
import getToggleWithoutContainerField from './Fields/getToggleWithoutContainerField';
import { isSustainabilityAvailable } from '../../Banners/SustainabilityBanner/sustainabilityUtils';

const SustainabilityBanner = dynamic(() => import('../../Banners/SustainabilityBanner'), { ssr: false });

type Index = {
  searchMode: SearchModes;
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  isMap: boolean;
  isBeachHoliday: boolean;
  commonData: CommonData;
  localization: Localization;
  currency: string;
  curiosityParams: CuriosityParams;
  isSidebar?: boolean;
  isWaiting: boolean;
  isHorizontal?: boolean;
  isListView?: boolean;
  productsData?: ProductsData;
  dynamicFiltersResponse?: DynamicFiltersResponse;
  isMobile: boolean;
  hasPreselectedFlight: boolean;
  hasPrefilterAirport?: boolean;
  t: TranslationFunction;
};

const verticalFilters = ({
  searchMode,
  searchParams,
  searchInfoData,
  searchInfoDetailData,
  isMap,
  isBeachHoliday,
  commonData,
  localization,
  curiosityParams,
  isSidebar,
  isWaiting,
  isHorizontal,
  isListView,
  productsData,
  dynamicFiltersResponse,
  isMobile,
  hasPreselectedFlight,
  hasPrefilterAirport = false,
  t,
}: Index): ControlProps[] => {
  const {
    imageBasePath,
    configValuesApp: { hideHotelChainFilter },
  } = localization;
  const isDP = searchMode === SearchModes.DP;

  const freeCancellationTextForProduct =
    searchMode === SearchModes.HO
      ? t('vacation.oss.filters.free_cancellation.text')
      : t('vacation.oss.filters.free_cancellation.text.dp');
  const isTransportEnabled = productsData?.transport?.usecaseEnabled;
  const airports = productsData?.transport?.airports;
  const hasMultipleAirports = Array.isArray(airports) && airports.length > 1;

  const offers = filterAvailability({ searchParams, searchInfoData, localization });

  const showDestinationAirportField =
    ((isTransportEnabled && hasMultipleAirports) || (isTransportEnabled && isWaiting && hasPrefilterAirport)) &&
    offers(FILTER_NAME.DESTINATION_AIRPORT);
  const showOvernightFilter =
    getShowOvernightFilter({ localization, transport: productsData?.transport, searchParams }) &&
    searchMode === SearchModes.DP &&
    offers(FILTER_NAME.OVERNIGHT_FLIGHTS);
  const transportData = productsData?.transport?.data;
  const accData = productsData?.accData;
  const depositAmountEnabled = localization?.configValuesApp?.depositAmountEnabled;

  const showPropertyNameFilter =
    !!productsData && !isEmptyObject(productsData) && shouldShowPropertyNameFilter(searchParams);

  const freeCancellationHtml = () => ({ __html: freeCancellationTextForProduct.replace(/\*(.*?)\*/g, '<b>$1</b>') });
  const blueFlagHtml = () => ({ __html: t('vacation.oss.filters.blue_flag.text').replace(/\*(.*?)\*/g, '<b>$1</b>') });
  const sustainabilityCertificateTitle = t(SUSTAINABILITY_CERTIFICATE_COPY.filterTitle);
  const sustainabilityCertificateText = t(SUSTAINABILITY_CERTIFICATE_COPY.filterText);
  const sustainabilityCertificateHtml = () => ({
    __html: sustainabilityCertificateText.replace(/\*(.*?)\*/g, '<b>$1</b>'),
  });
  const commonProps: CommonProps = {
    isMobile,
    isWaiting,
    isSidebar,
    isHorizontal,
    clearAll: t('vacation.oss.filters.clear'),
    ...(dynamicFiltersResponse ? { dynamicFiltersResponse } : {}),
  };
  const facilitiesField = getFacilitiesField({ searchParams, commonProps, t });
  const propertyTypesField = getPropertyTypesField({
    searchParams,
    commonProps,
    t,
  });
  const priceField = getPriceField({
    fieldName: URLParameters.PRICE,
    searchParams,
    searchMode,
    localization,
    t,
    commonProps,
  });
  const hideFreeCancellation =
    !isMap || (!searchParams[URLParameters.FREE_CANCELLATION] && dynamicFiltersResponse?.freeCancellation === 0);
  const hideBookNowPayLater =
    !searchParams[URLParameters.BOOK_NOW_PAY_LATER] && dynamicFiltersResponse?.bookNowPayLater === 0;

  const freeCancellationField = getToggleWithoutContainerField({
    fieldName: URLParameters.FREE_CANCELLATION,
    labelText: freeCancellationHtml(),
    value: searchParams[URLParameters.FREE_CANCELLATION] === '1',
    ...(dynamicFiltersResponse ? { count: dynamicFiltersResponse?.freeCancellation } : {}),
    ...(!searchParams[URLParameters.FREE_CANCELLATION]
      ? { disabled: dynamicFiltersResponse?.freeCancellation === 0 }
      : {}),
    commonProps,
  });
  const bookNowPayLaterField = getToggleWithoutContainerField({
    fieldName: URLParameters.BOOK_NOW_PAY_LATER,
    labelText: {
      __html: depositAmountEnabled
        ? t('vacation.oss.filters.book_now_pay_later_amount.text')
        : t('vacation.oss.filters.book_now_pay_later.text'),
    },
    value: searchParams?.[URLParameters.BOOK_NOW_PAY_LATER] === '1',
    ...(dynamicFiltersResponse ? { count: dynamicFiltersResponse?.bookNowPayLater } : {}),
    ...(!searchParams[URLParameters.BOOK_NOW_PAY_LATER]
      ? { disabled: dynamicFiltersResponse?.bookNowPayLater === 0 }
      : {}),
    commonProps,
  });
  const flightField = getFlightField({ searchParams, t, searchMode, commonProps });
  const leisureField = getLeisureField({ searchParams, t, commonProps });
  const starsField = getStarsField({ searchParams, t, commonProps });
  const mealPlanField = getMealPlanContainer({ searchParams, commonProps, t });
  const ratingField = getRatingField({
    searchParams,
    t,
    commonProps,
  });
  const blueFlagField = getToggleField({
    containerName: FilterContainer.BlueFlagContainer,
    fieldName: URLParameters.BLUE_FLAG,
    labelText: blueFlagHtml(),
    title: t('vacation.oss.filters.blue_flag.title'),
    hide: false,
    value: searchParams[URLParameters.BLUE_FLAG] === '1',
    commonProps,
  });
  // Same parse as the memsearch request, so '=true' deep links cannot filter while rendering unselected.
  const certificateApplied = getBooleanFromString(searchParams[URLParameters.SUSTAINABILITY_CERTIFICATE] ?? '');
  const hideSustainabilityCertificate =
    !offers(FILTER_NAME.SUSTAINABILITY_CERTIFICATE) ||
    !sustainabilityCertificateTitle ||
    !sustainabilityCertificateText ||
    // A filters API answer without the facet means the market backend lacks it — fail closed.
    // A facet that answered 0 keeps the toggle, greyed out; the neighbouring free cancellation and
    // book now pay later toggles hide at 0 instead, so this one deliberately differs from them.
    (!certificateApplied && !!dynamicFiltersResponse && dynamicFiltersResponse.sustainabilityCertificate === undefined);
  const sustainabilityCertificateField = getToggleField({
    containerName: FilterContainer.SustainabilityCertificateContainer,
    fieldName: URLParameters.SUSTAINABILITY_CERTIFICATE,
    labelText: sustainabilityCertificateHtml(),
    title: sustainabilityCertificateTitle,
    hide: hideSustainabilityCertificate,
    value: certificateApplied,
    count: dynamicFiltersResponse?.sustainabilityCertificate,
    disabled: disabledAtZero(certificateApplied, dynamicFiltersResponse?.sustainabilityCertificate),
    commonProps,
  });
  const beachTypeField = getBeachTypeField({ searchParams, t, commonProps });
  const climateField = getClimateField({ searchParams, t, commonProps });
  const faunaField = getFaunaField({ searchParams, t, commonProps });
  const waterSportsField = getWaterSportsField({ searchParams, t, commonProps });
  const entertainmentsField = getEntertainmentsField({ searchParams, t, commonProps });
  const waterWavesField = getWaterWavesField({ searchParams, t, commonProps });
  const discountFields = getDiscountFields({
    searchParams,
    searchInfoData,
    imageBasePath,
    searchMode,
    isWaiting,
    commonProps,
    commonData,
    localization,
    t,
  });
  const hideDiscountFields = discountFields?.controls?.length === 0;

  const bookingOptionsControls = [
    ...(freeCancellationField && !hideFreeCancellation && offers(FILTER_NAME.FREE_CANCELLATION)
      ? [freeCancellationField]
      : []),
    ...(bookNowPayLaterAvailable({ config: localization?.configValuesApp, searchMode, searchParams }) &&
    !hideBookNowPayLater &&
    offers(FILTER_NAME.BOOK_NOW_PAY_LATER)
      ? [bookNowPayLaterField]
      : []),
  ];
  const showBookingOptions = bookingOptionsControls.length > 0;

  const controls = [
    ...(isSustainabilityAvailable({
      localization,
      productsData,
      searchParams,
      hasPreselectedFlight,
    }) && isSidebar
      ? [
          {
            control: ControlType.EXTERNAL,
            component: () => <SustainabilityBanner />,
          },
        ]
      : []),
    ...(hideDiscountFields ? [] : [discountFields]),
    ...(showBookingOptions
      ? [
          {
            control: ControlType.CONTAINER,
            name: FilterContainer.BookingOptionsContainer,
            top: { text: t('vacation.oss.filters.booking_options.title') },
            controls: bookingOptionsControls,
            hide: hideFreeCancellation && hideBookNowPayLater,
          },
        ]
      : []),
    ...(showDestinationAirportField
      ? [
          getDestinationAirportField({
            isWaiting: !!isWaiting,
            productsData,
            t,
            searchParams,
            commonProps,
          }),
        ]
      : []),
    ...(showOvernightFilter
      ? [
          getOvernightFlights({
            isWaiting,
            searchParams,
            transportData,
            accData,
            commonProps,
            localization,
            t,
          }),
        ]
      : []),
    priceField,
    ...(offers(FILTER_NAME.DIRECT_FLIGHT) ? [flightField] : []),
    ...(isBeachHoliday
      ? [
          blueFlagField,
          beachTypeField,
          climateField,
          faunaField,
          waterSportsField,
          entertainmentsField,
          waterWavesField,
        ]
      : []),
    sustainabilityCertificateField,
    propertyTypesField,
    ...(showPropertyNameFilter
      ? [
          getPropertyNameField({
            searchParams,
            curiosityParams,
            localization,
            t,
            commonProps,
          }),
        ]
      : []),
    leisureField,
    starsField,
    mealPlanField,
    ratingField,
    facilitiesField,
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accomodationBeaches
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_BEACH,
            containerName: FilterContainer.AccommodationBeachContainer,
            containerTitle: t('vacation.oss.filters.beach.title'),
            options: dynamicFiltersResponse?.accomodationBeaches,
            param: searchParams[URLParameters.ACCOMODATION_BEACH],
            commonProps,
            isSearchList: true,
            label: t('vacation.oss.filters.beach.placeholder'),
            noResultsText: t('vacation.oss.filters.districts.no_matches'),
            anyField: t('vacation.oss.filters.beach.any'),
          }),
        ]
      : []),
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accomodationSkilifts
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_SKILIFT,
            containerName: FilterContainer.AccommodationSkiliftContainer,
            containerTitle: t('vacation.oss.filters.skilift.title'),
            options: dynamicFiltersResponse?.accomodationSkilifts,
            param: searchParams[URLParameters.ACCOMODATION_SKILIFT],
            commonProps,
            isSearchList: true,
            label: t('vacation.oss.filters.skilift.placeholder'),
            noResultsText: t('vacation.oss.filters.districts.no_matches'),
            anyField: t('vacation.oss.filters.skilift.any'),
          }),
        ]
      : []),
    ...(isHorizontal
      ? [
          getSortControl({
            searchMode,
            searchParams,
            searchInfoDetailData,
            isListView: !!isListView,
            imageBasePath,
            localization,
            isMobile,
            t,
          }),
        ]
      : []),
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accommodationDistricts
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_DISTRICT,
            containerName: FilterContainer.AccommodationDistrictsContainer,
            containerTitle: t('vacation.oss.filters.districts.title'),
            options: dynamicFiltersResponse?.accommodationDistricts,
            param: searchParams[URLParameters.ACCOMODATION_DISTRICT],
            commonProps,
            isSearchList: true,
            label: t('vacation.oss.filters.districts.placeholder'),
            noResultsText: t('vacation.oss.filters.districts.no_matches'),
          }),
        ]
      : []),
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accommodationStations
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_STATION,
            containerName: FilterContainer.AccommodationStationsContainer,
            containerTitle: t('vacation.oss.filters.stations.title'),
            options: dynamicFiltersResponse?.accommodationStations,
            param: searchParams[URLParameters.ACCOMODATION_STATION],
            commonProps,
            isSearchList: true,
            label: t('vacation.oss.filters.stations.placeholder'),
            noResultsText: t('vacation.oss.filters.districts.no_matches'),
            anyField: t('vacation.oss.filters.stations.any'),
          }),
        ]
      : []),
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accommodationChains && !hideHotelChainFilter
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_CHAIN,
            containerName: FilterContainer.AccommodationChainsContainer,
            containerTitle: t('vacation.oss.filters.hotel_chain.title'),
            options: dynamicFiltersResponse?.accommodationChains,
            param: searchParams[URLParameters.ACCOMODATION_CHAIN],
            commonProps,
            isSearchList: true,
            label: t('vacation.oss.filters.hotel_chain.placeholder'),
            noResultsText: t('vacation.oss.filters.districts.no_matches'),
          }),
        ]
      : []),
    ...(dynamicFiltersResponse && dynamicFiltersResponse.accommodationThemes
      ? [
          getDynamicField({
            fieldName: URLParameters.ACCOMODATION_THEME,
            containerName: FilterContainer.AccommodationThemesContainer,
            containerTitle: t('vacation.oss.filters.hotel_theme.title'),
            options: getAccommodationThemesOptions({
              t,
              accommodationThemes: dynamicFiltersResponse.accommodationThemes,
              isWaiting,
            }),
            param: searchParams[URLParameters.ACCOMODATION_THEME],
            commonProps,
          }),
        ]
      : []),
  ];

  return controls;
};

export default verticalFilters;
